import {
  AddAddress,
  AddCollectionToCart,
  AddToCart,
  FlashSaleOrderSubmitted,
  IAddCollectionToCartEvent,
  IAddToCartEvent,
  IRetailerPurchaseEvent,
  IUserAndRetailer,
  OrderSubmitted,
  SignUp,
  ViewProduct,
} from "@faire/deprecated-retailer-visitor-shared/events";
import {
  getIncomeRangeFromRetailer,
  getTwitterClickId,
} from "@faire/deprecated-retailer-visitor-shared/initializers/facebook";
import {
  groupWithAddedProductItems,
  IProductItem,
} from "@faire/deprecated-retailer-visitor-shared/lib/cartTools";
import { getProductOptionWholesalePrice } from "@faire/deprecated-retailer-visitor-shared/lib/product";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { trackCartAddToCartClick } from "@faire/web-api--source/events/cart/click/addToCart";
import { trackCheckoutOwfPurchasePlacedClick } from "@faire/web-api--source/events/checkout/click/owfPurchasePlaced";
import { trackCollectionsAddCollectionToCartClick } from "@faire/web-api--source/events/collections/click/addCollectionToCart";
import { trackOrderSubmissionFirstPurchasePlacedClick } from "@faire/web-api--source/events/orderSubmission/click/firstPurchasePlaced";
import { trackOrderSubmissionPurchasePlacedClick } from "@faire/web-api--source/events/orderSubmission/click/purchasePlaced";
import { trackRetailersAddNewShippingAddressComplete } from "@faire/web-api--source/events/retailers/actionCategoryOther/addNewShippingAddressComplete";
import { trackRetailersViewProductContentView } from "@faire/web-api--source/events/retailers/view/viewProductContent";
import { trackSignupRetailerSignUpComplete } from "@faire/web-api--source/events/signup/actionCategoryOther/retailerSignUpComplete";
import { IFlashSale } from "@faire/web-api--source/indigofair/data/IFlashSale";
import { IFlashSaleItem } from "@faire/web-api--source/indigofair/data/IFlashSaleItem";
import { IPixelCustomDataParameters } from "@faire/web-api--source/indigofair/data/IPixelCustomDataParameters";
import { IProduct } from "@faire/web-api--source/indigofair/data/IProduct";
import { IRetailerLaunchWithFaire as IOpenWithFaire } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaire";
import { logError } from "@faire/web/common/logging";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { trackEvent } from "@faire/web/common/trackEvent";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import sum from "lodash/sum";
import sumBy from "lodash/sumBy";

/**
 * @deprecated prefer to use `useFacebookPixelEvents` or `withStores` instead
 */
export class FacebookPixelEvents {
  static get = singletonGetter(FacebookPixelEvents);

  constructor() {
    ViewProduct.subscribe(this.handleViewProduct);
    AddAddress.subscribe(this.handleAddAddress);
    OrderSubmitted.subscribe(this.handleOrderSubmitted);
    FlashSaleOrderSubmitted.subscribe(this.handleFlashSaleOrderSubmitted);
    AddToCart.subscribe(this.handleAddToCart);
    AddCollectionToCart.subscribe(this.handleAddCollectionToCart);
    SignUp.subscribe(this.handleSignup);
  }

  private handleViewProduct = (product: IProduct) => {
    if (!product.token) {
      return;
    }
    const money = getProductOptionWholesalePrice(product);
    // TODO: Migrate to `useRetailer` hook when migrated to hooks
    const retailer = getRetailer();

    const pixelCustomDataParams = IPixelCustomDataParameters.build({
      content_type: "product",
      content_ids: [product.token],
      value: money.amount_cents,
      currency: money.currency,
      store_type: retailer?.business_type,
      years_in_business: retailer?.reported_creation_time,
      annual_sales_range: getIncomeRangeFromRetailer(retailer),
      twitter_click_id: getTwitterClickId(),
    });

    trackRetailersViewProductContentView(pixelCustomDataParams);
  };

  private handleAddAddress = () => {
    const pixelCustomDataParams = IPixelCustomDataParameters.build({
      // TODO: Change to useRetailer hook when migrated to FC
      retailer_token: getRetailer()?.token ?? "",
      twitter_click_id: getTwitterClickId(),
    });
    trackRetailersAddNewShippingAddressComplete(
      getRetailer()?.token ?? "",
      pixelCustomDataParams
    );
  };

  private handleOrderSubmitted = (event: IRetailerPurchaseEvent) => {
    if (isEmpty(event.givenProductItems)) {
      return;
    }

    const totalValues = map(
      event.givenProductItems,
      ({ quantity = 0, price_cents = 0 }) => (quantity * price_cents) / 100
    );

    const pixelCustomDataParams = IPixelCustomDataParameters.build({
      content_type: "product",
      contents: this.getContentsFromProductItems(event.givenProductItems, {
        addToCart: false,
      }),
      value: sum(totalValues),
      currency: "USD",
      num_items: sumBy(event.givenProductItems, "quantity"),
      retailer_token: event.retailer?.token,
      store_type: event.retailer?.business_type,
      years_in_business: event.retailer?.reported_creation_time,
      annual_sales_range: getIncomeRangeFromRetailer(event.retailer),
      twitter_click_id: getTwitterClickId(),
    });
    trackOrderSubmissionPurchasePlacedClick(pixelCustomDataParams);

    if (event.retailer && !event.retailer.last_order_at) {
      trackOrderSubmissionFirstPurchasePlacedClick(pixelCustomDataParams);
    } else {
      logError(new Error("purchase data loss due to retailer being empty"));
    }

    if (
      event.retailer &&
      event.retailer.launch_with_faire?.status ===
        IOpenWithFaire.Status.ACCEPTED
    ) {
      trackCheckoutOwfPurchasePlacedClick(pixelCustomDataParams);
    }
  };

  private handleFlashSaleOrderSubmitted = (event: IFlashSale) => {
    const items = event.items ?? [];
    const orderValues = event.price_cents ? event.price_cents / 100 : 0;
    // TODO: Migrate to useRetailer hook when migrated to hooks
    const retailer = getRetailer();
    const pixelCustomDataParams = IPixelCustomDataParameters.build({
      content_type: "product",
      contents: this.getContentsFromFlashSaleItems(items),
      value: orderValues,
      currency: "USD",
      num_items: sumBy(items, (i) => i.quantity!),
      retailer_token: retailer?.token,
      store_type: retailer?.business_type,
      years_in_business: retailer?.reported_creation_time,
      annual_sales_range: getIncomeRangeFromRetailer(retailer),
      twitter_click_id: getTwitterClickId(),
    });
    trackOrderSubmissionPurchasePlacedClick(pixelCustomDataParams);
  };

  private handleAddToCart = (event: IAddToCartEvent) => {
    const items = groupWithAddedProductItems(event.added);
    const addedItems = items.filter((item) => item.quantity > 0);
    if (isEmpty(addedItems)) {
      return;
    }
    const addedTotal = addedItems.reduce(
      (acc, item) => acc + item.price_cents / 100,
      0
    );
    const pixelCustomDataParams = IPixelCustomDataParameters.build({
      content_type: "product",
      contents: this.getContentsFromProductItems(addedItems, {
        addToCart: true,
      }),
      value: addedTotal,
      currency: "USD",
      twitter_click_id: getTwitterClickId(),
    });
    trackCartAddToCartClick(pixelCustomDataParams);
  };

  private handleAddCollectionToCart = (event: IAddCollectionToCartEvent) => {
    trackCollectionsAddCollectionToCartClick();
    if (event.isQuickAdd) {
      trackEvent(
        `/retailer/collections/add-to-cart/quick-add/${event.collectionToken}`
      );
    } else {
      trackEvent(
        `/retailer/collections/add-to-cart/modal/${event.collectionToken}`
      );
    }
  };

  private handleSignup = (event: IUserAndRetailer) => {
    const pixelCustomDataParams = IPixelCustomDataParameters.build({
      retailer_token: event.retailer.token ?? "",
      store_type: event.retailer.business_type,
      years_in_business: event.retailer.reported_creation_time,
      annual_sales_range: getIncomeRangeFromRetailer(event.retailer),
      twitter_click_id: getTwitterClickId(),
    });
    trackSignupRetailerSignUpComplete(
      event.retailer.token ?? "",
      pixelCustomDataParams
    );
  };

  private getContentsFromProductItems = (
    products: IProductItem[],
    eventType: { addToCart: boolean }
  ) => {
    return map(products, ({ product_token, quantity, price_cents }) => ({
      id: product_token,
      quantity,
      item_price:
        (eventType.addToCart ? price_cents / quantity : price_cents) / 100,
    }));
  };

  private getContentsFromFlashSaleItems = (
    flashSaleItems: IFlashSaleItem[]
  ) => {
    return flashSaleItems.map(({ product_token, quantity, price_cents }) => ({
      id: product_token,
      quantity,
      item_price: (price_cents ? price_cents : 0) / 100,
    }));
  };
}
